import { useMemo } from "react";
import moment from "moment-timezone";
import { useDialog, useMobileMenu, usePlatformStore } from "@platform/store";
import { useFetchUserCircles } from "@core/features/circle";
import {
  getDSTAwareAbbreviationFromTimezone,
} from "@core/features/timezones";
import { useUserTimezoneOption } from "@platform/hooks/useUserTimezoneOption";
import { useCircleTimeslot } from "@platform/features/programCircle/hooks/useCircleTimeslot";
import { useChatVisibility } from "@platform/hooks/useChatVisibility";
import { useUserMeetings } from "@platform/hooks/useUserMeetings";
import { useProgram } from "@platform/hooks/useProgram";
import { programEventBus } from "@platform/events/program.events";
import { ProgramCircle } from "./ProgramCircle";

export function ProgramCircleContainer() {
  const { program } = useProgram();
  const user = usePlatformStore((s) => s.user);

  const isAddToCalendarDisabled = usePlatformStore(
    (s) => s.featureFlags.isAddToCalendarDisabled
  );
  const isChatDisabled = usePlatformStore((s) => s.featureFlags.isChatDisabled);

  const { data: circlesData, isLoading: isLoadingCircles } =
    useFetchUserCircles({ user });
  const circles = circlesData?.circles ?? [];
  const circle = circles[0];

  const userTimezoneOption = useUserTimezoneOption();
  const { startTimeslot } = useCircleTimeslot();
  const { shouldShowChat } = useChatVisibility({ userCircles: circles, isChatDisabled });
  const { meetings } = useUserMeetings();
  const hasMeetings = meetings.length > 0;

  const { close: closeMobileMenu } = useMobileMenu();
  const { openDialog } = useDialog();

  const scheduledEngagementStartDate = program?.scheduled_engagement_start_date;

  const headerDescription = useMemo(() => {
    if (!startTimeslot) {
      return undefined;
    }

    if (!scheduledEngagementStartDate) {
      return undefined;
    }

    if (!userTimezoneOption) {
      return undefined;
    }

    return `Meeting on five ${startTimeslot.local.start.day}s at ${
      startTimeslot.local.start.formattedTime
    }${
      userTimezoneOption.abbreviation
        ? ` ${getDSTAwareAbbreviationFromTimezone(
            userTimezoneOption,
            startTimeslot.local.start.dateString
          )}`
        : ""
    }, starting the week of ${moment(scheduledEngagementStartDate).format(
      "MMM D"
    )}.`;
  }, [startTimeslot, userTimezoneOption, scheduledEngagementStartDate]);

  const handleOpenCircleDialog = () => {
    openDialog("circle");

    if (program && circle && program.id && circle.id) {
      programEventBus.emitEvent("program:circleModal:open", {
        programId: program.id,
        circleId: circle.id,
      });
    }

    closeMobileMenu();
  };

  const handleOpenAddCalendarEventsDialog = () => {
    openDialog("addCalendarEvents");

    if (program && circle && program.id && circle.id) {
      programEventBus.emitEvent("program:addToCalendarModal:open", {
        programId: program.id,
        circleId: circle.id,
      });
    }

    closeMobileMenu();
  };

  if (!program) return null;

  // Don't render until circles data is loaded
  if (isLoadingCircles) return null;

  // Don't render if user has no circles
  if (circles.length === 0) return null;

  return (
    <ProgramCircle
      circle={circle}
      isAddToCalendarDisabled={isAddToCalendarDisabled ?? false}
      shouldShowChat={shouldShowChat}
      hasMeetings={hasMeetings}
      headerDescription={headerDescription}
      onOpenCircleDialog={handleOpenCircleDialog}
      onOpenAddCalendarEventsDialog={handleOpenAddCalendarEventsDialog}
    />
  );
}

export default ProgramCircleContainer;
